import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";

export default function AdminEvents() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [mode, setMode] = useState("add"); // "add" | "edit"
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: "",
    event_date: "",
    start_time: "",
    end_time: "",
    location: "",
    is_active: 1,
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/api/admin/events");
      setItems(data || []);
      setErr("");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setMode("add");
    setEditingId(null);
    setForm({
      title: "",
      description: "",
      event_date: "",
      start_time: "",
      end_time: "",
      location: "",
      is_active: 1,
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!form.title.trim()) {
      setErr("Title is required");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        is_active: form.is_active ? 1 : 0,
      };

      if (mode === "add") {
        await api.post("/api/admin/events", payload);
      } else {
        await api.put(`/api/admin/events/${editingId}`, payload);
      }

      resetForm();
      await loadData();
      alert("Event saved successfully");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (item) => {
    setMode("edit");
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      description: item.description || "",
      event_date: item.event_date || "",
      start_time: item.start_time || "",
      end_time: item.end_time || "",
      location: item.location || "",
      is_active: item.is_active ? 1 : 0,
    });
  };

  const onDelete = async (item) => {
    if (!window.confirm("Mark this event as inactive?")) return;
    try {
      await api.delete(`/api/admin/events/${item.id}`);
      await loadData();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to delete event");
    }
  };

  const styles = {
    page: { minHeight: "100vh", background: "#f8fafc" },
    container: { maxWidth: 1100, margin: "0 auto", padding: 16 },
    card: {
      background: "#fff",
      borderRadius: 12,
      padding: 16,
      boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
      marginBottom: 16,
    },
    label: { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 },
    input: {
      width: "100%",
      padding: "8px 10px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
    },
    textarea: {
      width: "100%",
      padding: "8px 10px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
      minHeight: 70,
    },
    buttonPrimary: {
      background: "#0f766e",
      color: "#fff",
      border: "none",
      borderRadius: 999,
      padding: "8px 16px",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
    },
    buttonGhost: {
      background: "#e2e8f0",
      color: "#0f172a",
      border: "none",
      borderRadius: 999,
      padding: "8px 16px",
      fontSize: 14,
      fontWeight: 500,
      cursor: "pointer",
    },
    pill: (active) => ({
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      background: active ? "#d1fae5" : "#fee2e2",
      color: active ? "#166534" : "#991b1b",
      border: `1px solid ${active ? "#22c55e" : "#ef4444"}`,
    }),
  };

  const formatDate = (d) => d || "-";

  return (
    <div style={styles.page}>
      <SiteHeader showAdmin />
      <div style={styles.container}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
            Events (Home Page)
          </h2>
          <button
            type="button"
            style={styles.buttonGhost}
            onClick={() => navigate("/dashboard")}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Form */}
        <div style={styles.card}>
          <h3 style={{ marginTop: 0, fontSize: 16, marginBottom: 8 }}>
            {mode === "add" ? "Add New Event" : "Edit Event"}
          </h3>

          {err && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{err}</div>}

          <form onSubmit={onSubmit}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr",
                gap: 12,
              }}
            >
              <div style={{ gridColumn: "1 / span 3" }}>
                <label style={styles.label}>Title *</label>
                <input
                  style={styles.input}
                  name="title"
                  value={form.title}
                  onChange={onChange}
                  placeholder="Event title"
                />
              </div>

              <div style={{ gridColumn: "1 / span 3" }}>
                <label style={styles.label}>Description</label>
                <textarea
                  style={styles.textarea}
                  name="description"
                  value={form.description}
                  onChange={onChange}
                  placeholder="Short description of the event"
                />
              </div>

              <div>
                <label style={styles.label}>Event Date</label>
                <input
                  style={styles.input}
                  type="date"
                  name="event_date"
                  value={form.event_date || ""}
                  onChange={onChange}
                />
              </div>
              <div>
                <label style={styles.label}>Start Time</label>
                <input
                  style={styles.input}
                  type="time"
                  name="start_time"
                  value={form.start_time || ""}
                  onChange={onChange}
                />
              </div>
              <div>
                <label style={styles.label}>End Time</label>
                <input
                  style={styles.input}
                  type="time"
                  name="end_time"
                  value={form.end_time || ""}
                  onChange={onChange}
                />
              </div>

              <div style={{ gridColumn: "1 / span 2" }}>
                <label style={styles.label}>Location</label>
                <input
                  style={styles.input}
                  name="location"
                  value={form.location}
                  onChange={onChange}
                  placeholder="Event location"
                />
              </div>

              <div>
                <label style={styles.label}>Active</label>
                <div style={{ marginTop: 4 }}>
                  <label style={{ fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={!!form.is_active}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          is_active: e.target.checked ? 1 : 0,
                        }))
                      }
                    />{" "}
                    Show on Home page
                  </label>
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 16,
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              <button
                type="submit"
                style={styles.buttonPrimary}
                disabled={saving}
              >
                {saving
                  ? "Saving…"
                  : mode === "add"
                  ? "Add Event"
                  : "Update Event"}
              </button>
              {mode === "edit" && (
                <button
                  type="button"
                  style={styles.buttonGhost}
                  onClick={resetForm}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List */}
        <div style={styles.card}>
          <h3 style={{ marginTop: 0, fontSize: 16, marginBottom: 8 }}>
            Existing Events
          </h3>

          {loading && <div>Loading events…</div>}
          {!loading && items.length === 0 && (
            <div style={{ fontSize: 13, color: "#64748b" }}>
              No events yet. Add one above.
            </div>
          )}

          {!loading && items.length > 0 && (
            <div
              style={{
                display: "grid",
                gap: 8,
              }}
            >
              {items.map((ev) => (
                <div
                  key={ev.id}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    padding: 10,
                    display: "grid",
                    gridTemplateColumns: "2.2fr 1.3fr 0.7fr 0.8fr",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontSize: 13 }}>
                    <div style={{ fontWeight: 600 }}>{ev.title}</div>
                    {ev.description && (
                      <div
                        style={{
                          marginTop: 2,
                          color: "#64748b",
                          maxHeight: 40,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {ev.description}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <div>
                      <strong>Date:</strong> {formatDate(ev.event_date)}
                    </div>
                    {ev.start_time && (
                      <div>
                        <strong>Start:</strong> {ev.start_time}
                      </div>
                    )}
                    {ev.end_time && (
                      <div>
                        <strong>End:</strong> {ev.end_time}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <div>
                      <strong>Location:</strong>{" "}
                      {ev.location || <span style={{ color: "#9ca3af" }}>-</span>}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <span style={styles.pill(ev.is_active === 1)}>
                        {ev.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 4,
                    }}
                  >
                    <button
                      type="button"
                      style={{
                        ...styles.buttonGhost,
                        padding: "4px 10px",
                        fontSize: 12,
                      }}
                      onClick={() => onEdit(ev)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      style={{
                        ...styles.buttonGhost,
                        padding: "4px 10px",
                        fontSize: 12,
                        background: "#fee2e2",
                        color: "#b91c1c",
                      }}
                      onClick={() => onDelete(ev)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
